import { Google, Window } from '@mui/icons-material';
import { Box, Button, CircularProgress, SxProps, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Styles } from 'common/types';
import { Field } from 'common/ui/field';
import TextField from 'common/ui/text-field';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { selectInviteExist, selectInviteLoading } from 'modules/team-invite/selectors';
import { ReactComponent as FirstItem } from 'common/svg/1.svg';
import { ReactComponent as SecondItem } from 'common/svg/2.svg';
import { selectLoginUrl } from 'common/store/selectors';

const getStyles = (): Styles => ({
  card: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    cursor: 'pointer',
  },
  cardContent: {
    margin: 'auto',
    width: '100%',
    maxWidth: '600px',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    boxShadow: '0 2px 5px rgb(0 0 0 / 5%)',
    p: '45px 53px',
    boxSizing: 'border-box',
    '@media (max-width:780px)': {
      p: '45px 20px',
    },
  },
  fieldRowContainer: { display: 'flex', flexDirection: 'row' },
  fieldContainer: { width: '100%', m: '0 5px', mt: 2 },
  inputContainer: { display: 'flex', alignItems: 'center' },
  input: { flexGrow: 1 },
  label: { fontSize: 12, marginBottom: 1 },
  ownershipTitle: { fontSize: 12, marginBottom: 1, mt: 2 },
  methodButtons: { display: 'flex', flexDirection: 'column', mt: 1, width: '100%' },
  methodButton: { textTransform: 'none', borderRadius: '10px' },
  statusTitle: { mt: 1, fontWeight: 600 },
  statusText: { mt: 0.5, color: '#214254' },
  socialHint: { mt: 1, fontSize: 12, color: '#4288F0' },
  successHint: { mt: 1, fontSize: 12, color: '#2e7d32', fontWeight: 600 },
  bttnWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    mt: 1,
  },
  saveBttn: {
    border: '1px solid #C2DEEB',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '116px',
    height: '47px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#214254',
    fontSize: '16px',
    cursor: 'pointer',
  },
  reset: {
    cursor: 'pointer',
    fontSize: '12px',
    color: '#4288F0',
  },
});

const StyledField: SxProps = {
  width: '100%',
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: 'none' },
  },
};

interface TeamInviteFormValues {
  firstName: string;
  lastName: string;
  password: string;
}

type SocialOwnershipProvider = Exclude<TeamInviteModule.InviteOwnershipMethod, 'password'>;

const SOCIAL_SIGNUP_PROGRESS_KEY = 'team-invite-social-auth-in-progress';
const SOCIAL_SIGNUP_PROVIDER_KEY = 'team-invite-social-provider';
const INVITE_CODE_STORAGE_KEY = 'team-invite-code';

const FirstImageWrapper = styled.div`
  position: absolute;
  bottom: 12px;
  left: 81px;
  overflow: hidden;
  transition: opacity 0.3s ease-in-out;

  @media (max-width: 1350px) {
    display: none;
  }
`;

const SecondImageWrapper = styled.div`
  position: absolute;
  top: 56px;
  right: 81px;
  overflow: hidden;
  transition: opacity 0.3s ease-in-out;

  @media (max-width: 1350px) {
    display: none;
  }
`;

interface Props {
  availableMethods: TeamInviteModule.InviteOwnershipMethod[];
  selectedMethod: TeamInviteModule.InviteOwnershipMethod;
  onMethodChange: (method: TeamInviteModule.InviteOwnershipMethod) => void;
  onSocialVerified: (payload: { token: string; provider: SocialOwnershipProvider }) => void;
  onSocialError: () => void;
  isSocialVerified: boolean;
  socialProvider: SocialOwnershipProvider | null;
  isSubmitting: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getProfileNames = (socialUser: any): { firstName: string; lastName: string } => {
  const firstName = socialUser?.given_name || socialUser?.name?.split(' ')?.[0] || '';
  const lastName = socialUser?.family_name || socialUser?.name?.split(' ')?.slice(1).join(' ') || '';
  return { firstName, lastName };
};

export function TeamInviteContent({
  availableMethods,
  selectedMethod,
  onMethodChange,
  onSocialVerified,
  onSocialError,
  isSocialVerified,
  socialProvider,
  isSubmitting,
}: Props): ReactElement {
  const styles = getStyles();
  const { submitForm, setFieldValue } = useFormikContext<TeamInviteFormValues>();
  const { user, loginWithRedirect, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const socialHandledRef = useRef(false);

  const loading = useSelector(selectInviteLoading);
  const isExist = useSelector(selectInviteExist);
  const loginUrl = useSelector(selectLoginUrl);
  const socialMethods = useMemo(
    () => availableMethods.filter(method => method !== 'password') as SocialOwnershipProvider[],
    [availableMethods]
  );

  const url = 'https://brand.prospected.ai/';

  const handleOpenLogin = () => {
    window.open(loginUrl !== '' ? loginUrl : url);
  };

  const completeSocialVerification = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (socialUser: any, provider: SocialOwnershipProvider) => {
      const token = await getAccessTokenSilently();
      if (!token) {
        throw new Error('Unable to verify your social account. Please sign in again.');
      }

      if (!isExist) {
        const { firstName, lastName } = getProfileNames(socialUser);
        if (firstName) {
          setFieldValue('firstName', firstName, true);
        }
        if (lastName) {
          setFieldValue('lastName', lastName, true);
        }
      }

      onSocialVerified({ token, provider });
    },
    [getAccessTokenSilently, isExist, onSocialVerified, setFieldValue]
  );

  const handleSocialSignIn = useCallback(
    async (provider: SocialOwnershipProvider) => {
      if (isSubmitting) return;
      try {
        onMethodChange(provider);
        socialHandledRef.current = false;
        const inviteCode = new URLSearchParams(window.location.search).get('code');
        if (inviteCode) {
          sessionStorage.setItem(INVITE_CODE_STORAGE_KEY, inviteCode);
        }
        sessionStorage.setItem(SOCIAL_SIGNUP_PROGRESS_KEY, 'true');
        sessionStorage.setItem(SOCIAL_SIGNUP_PROVIDER_KEY, provider);
        await loginWithRedirect({
          appState: {
            returnTo: `${window.location.pathname}${window.location.search}`,
          },
          authorizationParams: {
            connection: provider,
            redirect_uri: `${window.location.origin}${window.location.pathname}`,
          },
        });
      } catch {
        sessionStorage.removeItem(SOCIAL_SIGNUP_PROGRESS_KEY);
        sessionStorage.removeItem(SOCIAL_SIGNUP_PROVIDER_KEY);
        onSocialError();
      }
    },
    [isSubmitting, loginWithRedirect, onMethodChange, onSocialError]
  );

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const isInProgress = sessionStorage.getItem(SOCIAL_SIGNUP_PROGRESS_KEY) === 'true';
    const provider = sessionStorage.getItem(SOCIAL_SIGNUP_PROVIDER_KEY) as SocialOwnershipProvider | null;

    if (!isInProgress || !provider || socialHandledRef.current) return;
    socialHandledRef.current = true;

    completeSocialVerification(user, provider).catch(() => {
      onSocialError();
    });

    sessionStorage.removeItem(SOCIAL_SIGNUP_PROGRESS_KEY);
    sessionStorage.removeItem(SOCIAL_SIGNUP_PROVIDER_KEY);
  }, [completeSocialVerification, isAuthenticated, onSocialError, user]);

  const hasPasswordMethod = availableMethods.includes('password');
  const showOwnershipOptions = availableMethods.length > 0;
  const hasOnlyPasswordOption = isExist && availableMethods.length === 1 && availableMethods[0] === 'password';
  const showMethodPicker = showOwnershipOptions && socialMethods.length > 0 && !hasOnlyPasswordOption;
  const isSocialMethodSelected = selectedMethod !== 'password';
  const isSelectedSocialVerified = isSocialMethodSelected && isSocialVerified && socialProvider === selectedMethod;
  const shouldDisablePasswordForSocialNewAccount = !isExist && isSelectedSocialVerified;
  const showSocialSigninButtons = !isSelectedSocialVerified;
  let submitButtonContent: ReactElement | string = isExist ? 'Submit' : 'Save';
  if (isSubmitting) {
    submitButtonContent = <CircularProgress size={22} color="inherit" />;
  }

  let statusTitle = 'Verify your existing account';
  let statusDescription = 'Use your existing password below, or continue with social sign-in.';

  if (!isExist) {
    statusTitle = 'Create your account from invitation';
    statusDescription = 'Enter your details below, or continue with social sign-up.';
  } else if (hasOnlyPasswordOption) {
    statusDescription = 'Enter your existing password to continue.';
  } else if (isSelectedSocialVerified) {
    statusTitle = 'Account verified';
    statusDescription = 'Your social account is verified. Click Submit to continue.';
  } else if (isSocialMethodSelected) {
    statusDescription = 'Sign in with your social account to verify ownership, then click Submit.';
  }

  return (
    <Box sx={styles.card}>
      {loading ? (
        <CircularProgress color="primary" />
      ) : (
        <Box sx={styles.cardContent}>
          <Typography variant="h5" sx={styles.statusTitle}>
            {statusTitle}
          </Typography>
          <Typography sx={styles.statusText}>{statusDescription}</Typography>

          {!isExist && (
            <Box sx={styles.fieldRowContainer}>
              <Box sx={styles.fieldContainer}>
                <Typography sx={styles.label}>First Name</Typography>
                <Box sx={styles.inputContainer}>
                  <Field component={TextField} name="firstName" placeholder="John" sx={{ ...StyledField }} />
                </Box>
              </Box>
              <Box sx={styles.fieldContainer}>
                <Typography sx={styles.label}>Last Name</Typography>
                <Box sx={styles.inputContainer}>
                  <Field component={TextField} name="lastName" placeholder="Smith" sx={{ ...StyledField }} />
                </Box>
              </Box>
            </Box>
          )}
          {hasPasswordMethod && (
            <Box sx={styles.fieldContainer}>
              <Typography sx={styles.label}>{isExist ? '' : 'Password'}</Typography>
              <Box sx={styles.inputContainer}>
                <Field
                  component={TextField}
                  type="password"
                  name="password"
                  placeholder="Password"
                  disabled={shouldDisablePasswordForSocialNewAccount}
                  onFocus={() => onMethodChange('password')}
                  sx={{ ...StyledField }}
                />
              </Box>
            </Box>
          )}
          {isExist && hasPasswordMethod && (
            <Box sx={styles.fieldContainer} onClick={handleOpenLogin}>
              <Typography sx={styles.reset}>Forgot your password? Reset on the Login Screen</Typography>
            </Box>
          )}
          {showMethodPicker && (
            <Box sx={styles.fieldContainer}>
              {!isExist && showSocialSigninButtons && (
                <Typography sx={styles.ownershipTitle}>Alternative sign-up method</Typography>
              )}
              <Box sx={styles.methodButtons}>
                {showSocialSigninButtons && socialMethods.includes('google-oauth2') && (
                  <Button
                    size="small"
                    color="secondary"
                    variant="outlined"
                    fullWidth
                    startIcon={<Google />}
                    sx={{ mb: socialMethods.includes('windowslive') ? 2 : 0, py: 1.5 }}
                    onClick={() => handleSocialSignIn('google-oauth2')}
                  >
                    Sign In with Google
                  </Button>
                )}
                {showSocialSigninButtons && socialMethods.includes('windowslive') && (
                  <Button
                    size="small"
                    color="secondary"
                    variant="outlined"
                    fullWidth
                    startIcon={<Window />}
                    sx={{ py: 1.5 }}
                    onClick={() => handleSocialSignIn('windowslive')}
                  >
                    Sign In with Microsoft
                  </Button>
                )}
              </Box>
              {isSelectedSocialVerified && <Typography sx={styles.successHint}>Social sign-in successful</Typography>}
            </Box>
          )}
          <Box sx={styles.bttnWrapper}>
            <Box
              sx={{
                ...styles.saveBttn,
                opacity: isSubmitting ? 0.7 : 1,
                pointerEvents: isSubmitting ? 'none' : 'auto',
              }}
              onClick={submitForm}
            >
              {submitButtonContent}
            </Box>
          </Box>
        </Box>
      )}
      <>
        <FirstImageWrapper>
          <FirstItem />
        </FirstImageWrapper>
        <SecondImageWrapper>
          <SecondItem />
        </SecondImageWrapper>
      </>
    </Box>
  );
}
